// @flow
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  push,
  replace,
  go,
  goBack,
  goForward,
} from "../redux/_router/RouterActions";

import type { Location, LocationShape } from "react-router";
import type {
  GoAction,
  GoBackAction,
  GoForwardAction,
  PushAction,
  ReplaceAction,
} from "../redux";

type NavigationAPI = {|
  push: (location: LocationShape | string, state?: { ... }) => PushAction,
  replace: (location: LocationShape | string, state?: { ... }) => ReplaceAction,
  go: (delta: number) => GoAction,
  goBack: () => GoBackAction,
  goForward: () => GoForwardAction,
|};

/**
 * Returns the full location object from the router state.
 */
export const useLocation = (): Location =>
  useSelector((state) => state.router?.location || {});

/**
 * Returns the unique key of the current location.
 */
export const useLocationKey = (): string =>
  useSelector((state) => state.router?.location?.key ?? "");

/**
 * Returns the current search (querystring) portion of the URL.
 */
export const useQuerystring = (): string =>
  useSelector((state) => state.router?.location?.search ?? "");

/**
 * Returns the current URL pathname.
 */
export const usePathname = (): string =>
  useSelector((state) => state.router?.location?.pathname ?? "");

/**
 * Provides navigation methods.
 */
export const useNavigation = (): NavigationAPI => {
  const dispatch = useDispatch();

  return useMemo(
    () => ({
      push: (location, state) => dispatch(push(location, state)),
      replace: (location, state) => dispatch(replace(location, state)),
      go: (delta) => dispatch(go(delta)),
      goBack: () => dispatch(goBack()),
      goForward: () => dispatch(goForward()),
    }),
    [dispatch],
  );
};
